/**
 * @fileoverview Top-level web component for the application.
 *
 * This mostly provides context for everything else, and is responsible for creating the overall
 * layout of the application.
 */

import { elt } from './dom';
import { RebuildMenu } from './RebuildMenu';
import { RebuildNavigation } from './RebuildNavigation';
import { RebuildContentPane } from './RebuildContentPane';
import { ContentSection, NavigationEvent } from './AppState';
import { DevicesActivityPanel } from './devices/devices-activity-panel';
import { Placeholder } from './components/placeholder';

export class RebuildApp extends HTMLElement {
  menu: RebuildMenu;
  navigation: RebuildNavigation;
  contentPane: RebuildContentPane;

  currentActivity: ContentSection;
  _devicesPanel: DevicesActivityPanel;

  constructor() {
    super();
    this.currentActivity = ContentSection.Pages;
  }

  connectedCallback() {
    this.menu = document.createElement('rebuild-menu') as RebuildMenu;
    this.navigation = document.createElement('rebuild-navigation') as RebuildNavigation;
    this.contentPane = document.createElement('rebuild-content-pane') as RebuildContentPane;

    const menuContainer = elt('div', { class: 'rebuild-menu-container' }, this.menu);
    const navContainer = elt('div', { class: 'rebuild-navigation-container' }, this.navigation);
    const contentContainer = elt('div', { class: 'rebuild-content-container' }, this.contentPane);
    const bodyContainer = elt('div', { class: 'rebuild-body-container' }, navContainer, contentContainer);

    this.append(menuContainer, bodyContainer);
    this.setActivity(this.currentActivity);

    this.addEventListener(NavigationEvent.eventType, this);
  }

  handleEvent(e: Event): void {
    if (e.type === NavigationEvent.eventType) {
      const event = e as NavigationEvent;
      this.setActivity(event.activity);
    }
  }

  setActivity(activity: ContentSection): void {
    this.currentActivity = activity;
    this.navigation.setActiveActivity(activity);

    if (activity == ContentSection.Devices) {
      this.contentPane.setContent(this.devicesPanel);
    }
    else {
      this.contentPane.setActivity(activity);
    }
  }

  get devicesPanel(): DevicesActivityPanel {
    if (!this._devicesPanel) {
      this._devicesPanel = elt('rebuild-devices-activity-panel');
    }

    return this._devicesPanel;
  }
}

// This doesn't make sense to me or I'm missing something about how ES Module imports work, but
// leaving the customElements.define(..) calls for each component at the bottom of the component
// file isn't working when I import the exported class. That line gets skipped somehow; I'm not
// sure if it's TypeScript pulling it out or something else I don't understand. For now, I'm
// registering the components here.
//
// This file is ok because the whole file is imported, not just the RebuildApp export.
customElements.define('rebuild-app', RebuildApp);
customElements.define('rebuild-content-pane', RebuildContentPane);
customElements.define('rebuild-menu', RebuildMenu);
customElements.define('rebuild-navigation', RebuildNavigation);
DevicesActivityPanel.register();
Placeholder.register();